import React, { useEffect, useState } from "react";
import moment from "moment";
import { useConfirm } from "material-ui-confirm";
import { get, post, handleResponse } from "../../api";
import history from "../../helpers/history";
import Avatar from "../Avatar";
import Button from "../Button";
import FrameHeader from "../FrameHeader";
import styles from "./SignupRequests.module.scss";

const BASE = "/customers/domain/registration-attempt";

const fullName = (r) =>
  [r.first_name, r.last_name].filter(Boolean).join(" ") || r.email;

async function messageFrom(res, fallback) {
  const body = await res.json().catch(() => null);
  return (
    (body && body.responseStatus && body.responseStatus.message) || fallback
  );
}

/**
 * People who tried to sign up with this customer's email domain, shown at the
 * top of the Users page with Approve / Deny. The Approve button in the
 * administrator's email lands here with `?approve=token`: that request is
 * highlighted and waits for the click; approving is one click, no dialog.
 */
export default function SignupRequests({ approveToken, onApproved }) {
  const confirm = useConfirm();
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);
  const [highlighted, setHighlighted] = useState(null);

  /** A dialog with just OK, only for a link whose request is no longer open. */
  function say(text) {
    confirm({
      description: text,
      title: null,
      hideCancelButton: true,
      confirmationText: "OK",
      dialogProps: { fullScreen: false },
      confirmationButtonProps: { color: "primary", disableRipple: true },
    }).catch(() => {});
  }

  function drop(token) {
    setRequests((rs) => rs.filter((r) => r.token !== token));
  }

  async function approve(token, name) {
    setBusy(token);
    const res = await post(`${BASE}/${token}/approve`, {}, false);
    if (res && res.ok) {
      // Done: the row goes and the person appears in the users list below.
      drop(token);
      onApproved && onApproved();
    } else if (
      res &&
      (res.status === 409 || res.status === 404 || res.status === 403)
    ) {
      // Someone got there first, it was declined, or the link is stale.
      say(await messageFrom(res, "This request could not be approved."));
      drop(token);
    } else if (res) {
      await handleResponse(res);
    } else {
      say(`Sorry, ${name} could not be approved. Try again.`);
    }
    setBusy(null);
  }

  function deny(token, name) {
    confirm({
      description: `Decline the request from ${name}? They will not be added and the link in the email stops working.`,
      title: null,
      dialogProps: { fullScreen: false },
      cancellationButtonProps: { color: "error", disableRipple: true },
      confirmationButtonProps: { color: "primary", disableRipple: true },
    })
      .then(async () => {
        setBusy(token);
        const res = await post(`${BASE}/${token}/deny`, {}, false);
        if (res && res.ok) {
          drop(token);
        } else if (
          res &&
          (res.status === 409 || res.status === 404 || res.status === 403)
        ) {
          say(await messageFrom(res, "This request could not be declined."));
          drop(token);
        } else if (res) {
          await handleResponse(res);
        }
        setBusy(null);
      })
      .catch(() => {});
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const list = await get("/customers/domain/registration-attempts");
      if (cancelled) return;
      if (Array.isArray(list)) setRequests(list);

      if (approveToken) {
        /**
         * Arrived from the email: point at that request and let the
         * administrator click. If it is no longer open, say what happened to
         * it. Then take the token out of the address.
         */
        const known = Array.isArray(list)
          ? list.find((r) => r.token === approveToken)
          : null;
        if (known) {
          setHighlighted(approveToken);
        } else {
          const res = await get(`${BASE}/${approveToken}`, {}, false);
          if (cancelled) return;
          if (res && res.ok) {
            const r = await res.json();
            const when = (d) => moment(d).format("MMMM Do YYYY [at] h:mma");
            say(
              r.status === "approved"
                ? `${r.approved_by || "An administrator"} already approved ${fullName(r)} on ${when(r.approved_time)}.`
                : r.status === "denied"
                  ? `${r.denied_by || "An administrator"} declined the request from ${fullName(r)} on ${when(r.denied_time)}.`
                  : `The request from ${fullName(r)} has expired. Ask them to sign up again, or add them with New user.`,
            );
          } else if (res) {
            say(await messageFrom(res, "This approval link is not valid."));
          }
        }
        history.replace(history.location.pathname);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approveToken]);

  if (requests.length === 0) return null;

  return (
    <>
      <FrameHeader title="Sign up requests" />
      <div className={styles.list}>
        {requests.map((r) => (
          <div
            key={r.token}
            className={
              r.token === highlighted
                ? `${styles.row} ${styles.highlighted}`
                : styles.row
            }
          >
            <Avatar name={fullName(r)} theme="pending" />
            <div className={styles.text}>
              <p className={styles.title}>{fullName(r)}</p>
              <p className={styles.subtitle}>{r.email}</p>
            </div>
            <div className={styles.actions}>
              <Button
                theme="primary"
                isLoading={busy === r.token}
                disabled={busy !== null}
                onClick={() => approve(r.token, fullName(r))}
              >
                Approve
              </Button>
              <Button
                disabled={busy !== null}
                onClick={() => deny(r.token, fullName(r))}
              >
                Deny
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
